import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Trash2, Sparkles } from "lucide-react";
import { API, getAuthHeaders } from "@/App";
import StickerCreatorDialog from "./StickerCreatorDialog";

export default function StickerPickerDialog({ open, onOpenChange, conversationId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null);
  const [creatorOpen, setCreatorOpen] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/stickers`, { credentials: "include", headers: getAuthHeaders() });
      if (res.ok) setItems(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchAll();
  }, [open]);

  const send = async (sticker_id) => {
    if (!conversationId) return;
    setSending(sticker_id);
    try {
      const res = await fetch(`${API}/stickers/${sticker_id}/send`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ conversation_id: conversationId }),
      });
      if (!res.ok) throw new Error();
      onOpenChange(false);
    } catch {
      toast.error("No se pudo enviar el sticker");
    } finally {
      setSending(null);
    }
  };

  const remove = async (sticker_id) => {
    try {
      const res = await fetch(`${API}/stickers/${sticker_id}`, {
        method: "DELETE",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error();
      fetchAll();
    } catch {
      toast.error("No se pudo borrar");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#17212b] border-[#232e3c] text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-pink-400" /> Stickers
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Toca un sticker para enviarlo, o crea uno desde una foto.
            </DialogDescription>
          </DialogHeader>

          <Button onClick={() => setCreatorOpen(true)} className="bg-pink-600 hover:bg-pink-700 w-full mb-3">
            <Plus className="w-4 h-4 mr-2" /> Crear sticker desde foto
          </Button>

          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>
            ) : items.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-8">
                Aún no tienes stickers. Crea el primero desde una foto.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {items.map((s) => (
                  <div key={s.sticker_id} className="relative group rounded-lg bg-[#0e1621] border border-[#232e3c] p-2 hover:border-pink-500 transition">
                    <button
                      onClick={() => send(s.sticker_id)}
                      disabled={sending === s.sticker_id}
                      className="block w-full"
                      title={s.name}
                    >
                      <img src={s.image_url} alt={s.name} className="w-full h-20 object-contain" />
                      <div className="text-[11px] text-slate-400 truncate mt-1">{s.name}</div>
                    </button>
                    <button
                      onClick={() => remove(s.sticker_id)}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-black/60 rounded p-1 text-red-400"
                      title="Eliminar"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <StickerCreatorDialog
        open={creatorOpen}
        onOpenChange={setCreatorOpen}
        onCreated={() => {
          setCreatorOpen(false);
          fetchAll();
        }}
      />
    </>
  );
}
